import { DateTimeType } from './dateTypes';
import { ReceiptFile } from './receiptFile';
import { Substitute } from './substitute';
import { Transaction } from './transactions';
import { WorkflowState } from './workflow';
import { z, type ZodObject, type ZodRawShape } from 'zod';

const HistoryItemBase = z.object({
    /** Who did it (e.g. user id or system) */
    author: z.string(),
    /** The automated agent (e.g. 'mcp') that performed the action on behalf of `author`, if any */
    agent: z.string().nullish(),
    /** When it happened */
    date: DateTimeType,
    mergedCount: z.number().nullish(),
    mergedDate: DateTimeType.nullish(),
});

/** Author fields for a new history entry, carrying the acting user's automated agent, if any. */
export function historyAuthor(user: { id: string; agent?: string | null }) {
    return { author: user.id, agent: user.agent ?? undefined };
}

/**
 * Whether an existing history entry was authored by the same (user, agent) pair - so merging
 * consecutive entries never conflates a user's own changes with an agent acting for them.
 */
export function isSameHistoryAuthor(
    item: { author: string; agent?: string | null },
    user: { id: string; agent?: string | null },
) {
    return item.author === user.id && (item.agent ?? null) === (user.agent ?? null);
}

function create<Action extends string, Shape extends ZodRawShape>(
    action: Action,
    schema: ZodObject<Shape>,
) {
    return HistoryItemBase.extend({
        /** Type of change */
        action: z.literal(action),
        ...schema.shape,
    });
}

export const HistoryItem = z.discriminatedUnion('action', [
    // General
    create('create', z.object({ values: z.unknown() })),
    create('update', z.object({ diff: z.record(z.string(), z.tuple([z.unknown(), z.unknown()])) })),
    create('updateExpenses', z.object({})),
    create('delete', z.object({})),
    create('addComment', z.object({ comment: z.string() })),

    // Bucket
    create('addReceipt', z.object({ receiptIds: z.array(z.string()) })),
    create('removeReceipt', z.object({ receiptIds: z.array(z.string()) })),
    create('deleteReceipt', z.object({ receiptIds: z.array(z.string()) })),
    create(
        'transition',
        z.object({
            from: WorkflowState.catch('payment'),
            to: WorkflowState.catch('payment'),
            comment: z.string().optional(),
        }),
    ),
    create('archive', z.object({})),
    create('reactivate', z.object({})),
    create('transaction', Transaction),

    // Receipt
    create('addFiles', z.object({ files: z.array(ReceiptFile) })),
    create('removeFile', z.object({ fileIds: z.array(z.string()) })),
    create('addToTrip', z.object({ tripId: z.string() })),
    create('removeFromTrip', z.object({ tripId: z.string() })),
    create('acceptDuplicates', z.object({ comment: z.string().optional() })),
    create('reviewDuplicates', z.object({ comment: z.string().optional() })),
    create('resetDuplicates', z.object({})),

    // User
    create(
        'updateAdditionalApprovers',
        z.object({ additionalApproverIds: z.string().array().nullable() }),
    ),
    create('updateSubstitute', z.object({ substitute: Substitute })),
    create('deleteSubstitute', z.object({ substituteId: z.string() })),
]);

type _HistoryItem = z.infer<typeof HistoryItem>;
export type HistoryItem<TAction extends _HistoryItem['action'] = _HistoryItem['action']> = Extract<
    _HistoryItem,
    { action: TAction }
>;
